import { z } from "zod";

const proof = z.string().regex(/^[a-f0-9]{64}$/);
const secret = z.string().regex(/^[A-Za-z0-9_-]{43}$/);
export const SessionTicketRequestSchema = z.object({ challenge: proof, audience: z.enum(["web", "extension"]), webOrigin: z.string().url() }).strict();
export const SessionExchangeRequestSchema = z.object({ ticket: secret, verifier: z.string().uuid(), audience: z.enum(["web", "extension"]), webOrigin: z.string().url() }).strict();
export const SessionTicketSchema = z.object({ ticket: secret, familyId: z.string().uuid() }).strict();
export const SyncStateSchema = z.object({ id: z.string().uuid(), kind: z.enum(["login", "logout", "sync"]), userId: z.string().uuid().optional(), at: z.number().optional(), previousId: z.string().optional() }).strict();
export type SyncState = z.infer<typeof SyncStateSchema>;

const envelope = { source: z.literal("xiaoman-web-session"), version: z.literal(1), id: z.string().uuid(), apiOrigin: z.string().url() };
export const SessionBridgeRequestSchema = z.discriminatedUnion("action", [
  z.object({ ...envelope, action: z.literal("status") }).strict(),
  z.object({ ...envelope, action: z.literal("offer"), challenge: proof, expectedState: z.string(), userId: z.string().uuid().optional() }).strict(),
  z.object({ ...envelope, action: z.literal("prepare"), userId: z.string().uuid(), explicit: z.boolean() }).strict(),
  z.object({ ...envelope, action: z.literal("accept"), nonce: z.string().uuid(), ticket: secret }).strict(),
  z.object({ ...envelope, action: z.literal("logout"), userId: z.string().uuid(), expectedState: z.string() }).strict(),
]);
export type SessionBridgeRequest = z.infer<typeof SessionBridgeRequestSchema>;
export const SessionBridgeResultSchema = z.discriminatedUnion("kind", [
  z.object({ kind: z.literal("status"), userId: z.string().uuid().optional(), familyId: z.string().uuid().optional(), state: SyncStateSchema.optional() }).strict(),
  z.object({ kind: z.literal("ticket"), ticket: secret }).strict(),
  z.object({ kind: z.literal("prepared"), challenge: proof, nonce: z.string().uuid() }).strict(),
  z.object({ kind: z.literal("ok") }).strict(),
  z.object({ kind: z.literal("error"), code: z.enum(["UNAVAILABLE", "ENVIRONMENT_MISMATCH", "ACCOUNT_CONFLICT", "SIGNED_OUT", "STATE_CHANGED"]) }).strict(),
]);
export const SessionBridgeResponseSchema = z.object({ source: z.literal("xiaoman-extension-session"), version: z.literal(1), id: z.string().uuid(), result: SessionBridgeResultSchema }).strict();
export type SessionBridgeResult = z.infer<typeof SessionBridgeResultSchema>;
export type SessionBridgeResponse = z.infer<typeof SessionBridgeResponseSchema>;

export function normalizedApiOrigin(value: string): string {
  const url = new URL(value);
  if (url.hostname === "localhost") url.hostname = "127.0.0.1";
  return url.origin;
}
export async function sessionChallenge(verifier: string): Promise<string> {
  const hash = await crypto.subtle.digest("SHA-256", new TextEncoder().encode(verifier));
  return Array.from(new Uint8Array(hash), byte => byte.toString(16).padStart(2, "0")).join("");
}
